import { useAppSelector } from '../../store/hooks/reduxHooks';
import React, { useState, useEffect, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Calendar, Filter, ChevronDown, User, Store, Link2 } from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { OrderDetailModal } from './OrderDetailModal';
import type { OrderDetail, OrderDocument } from './OrderDetailModal';
import { OrderStatus, Order, fetchOrderById, fetchOrders } from '../../services/order';
import { showToast } from '../../utils/toast';
import type { OrderFilters } from '../../services/order';

// Helper to format date for input fields
const formatDateForInput = (date: string | null): string => {
  if (!date) return '';
  try {
    if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
      const [day, month, year] = date.split('-');
      return `${year}-${month}-${day}`;
    }
    return new Date(date).toISOString().split('T')[0];
  } catch {
    return '';
  }
};

// Format timestamp for display
const formatTimestamp = (timestamp: number | null | undefined): string => {
  if (!timestamp) return 'N/A';

  const date = new Date(timestamp);

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};


export function formatTimestampGateway(ts: number): string {
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
}

const OrderTab: React.FC = () => {
  // Table state
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalMatch, setTotalMatch] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState<OrderFilters>({});
  const [tempFilters, setTempFilters] = useState<OrderFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const user = useAppSelector(state => state.auth.user);
  console.log('Current user in OrderTab:', user);


  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const isFirstRender = React.useRef(true);

  // Fetch orders
  const fetchOrdersData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const lenderCode = user?.code ? String(user.code).toLowerCase() : '';

      // Extract dateRange from filters (if present)
      const { dateRange, ...otherFilters } = filters || {};

      const response = await fetchOrders({
        page,
        pageSize,
        sortBy,
        sortOrder,
        search: searchQuery,
        token,
        lender: lenderCode,
        filters: otherFilters,       // keep other filters clean
        dateRange: dateRange || {},  // send dateRange separately
      });

      setOrders(response.orders);
      setTotalMatch(response.pagination?.totalMatch ?? 0);
      setTotalCount(response.pagination?.totalCount ?? 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast.error('Failed to fetch orders');
      setOrders([]);
      setTotalMatch(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, filters, searchQuery]);


  useEffect(() => {
    fetchOrdersData();
  }, [fetchOrdersData]);

  useEffect(() => {
    if (isFirstRender.current) {
      fetchOrdersData();
      isFirstRender.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isFirstRender.current) fetchOrdersData();
  }, [page]);

  // Format currency
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  // Render status badge
  const renderStatusBadge = (status: OrderStatus) => {
    let bgColor = '';
    let textColor = '';
    switch (status) {
      case 'Verification Pending':
        bgColor = 'bg-indigo-100';
        textColor = 'text-indigo-800';
        break;
      case 'Completed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
    }
    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor} capitalize`}>
        {status}
      </span>
    );
  };

  const columns: ColumnDef<Order, any>[] = [
    {
      id: 'srNo',
      header: () => (
        <div className="text-center w-full">
          Sr. No
        </div>
      ),
      size: 60,
      cell: ({ row }: { row: any }) => (
        <div className="text-center w-full">
          {(page - 1) * pageSize + row.index + 1}
        </div>
      ),
    },

    { id: 'orderId', header: 'Order ID', accessorKey: 'orderId', size: 90 },
    // {
    //   id: "platform",
    //   header: () => (
    //     <div className="text-center w-full">Platform</div>
    //   ),
    //   accessorKey: "platform",
    //   size: 90,
    //   cell: ({ row }: { row: any }) => {
    //     const platform = row.original.platform?.toLowerCase();

    //     if (!platform) {
    //       return <div className="text-center text-gray-500 text-xs">N/A</div>;
    //     }

    //     const isMerchant = platform === "merchant";

    //     return (
    //       <div className="flex items-center justify-center gap-1.5 text-gray-500">
    //         {isMerchant ? (
    //           <Store className="w-3.5 h-3.5 text-gray-500" />
    //         ) : (
    //           <Link2 className="w-3.5 h-3.5 text-gray-500" />
    //         )}

    //         <span className="text-xs font-medium">
    //           {platform.toUpperCase()}
    //         </span>
    //       </div>
    //     );
    //   },
    // } ,
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 80,
      cell: ({ row }: { row: any }) => renderStatusBadge(row.original.status as OrderStatus),
    },
    {
      id: 'customer',
      header: 'Customer Info',
      accessorKey: 'customerId',
      size: 200, // increase column width
      cell: ({ row }: { row: any }) => (
        <div className="py-1">
          <div className="flex items-start text-xs font-medium gap-1">
            <User size={10} className="mt-[2px] text-gray-500 shrink-0" />
            <span
              className="whitespace-normal break-words leading-snug"
              title={row.original.settlement?.customerName || 'N/A'}
            >
              {row.original.settlement?.customerName || 'N/A'}
            </span>
          </div>

          <div className="text-xs text-gray-500 mt-[2px]">
            {row.original.settlement?.customerMobileNo || 'N/A'}
          </div>
        </div>
      )
    },

    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      size: 80,
      cell: ({ row }: { row: any }) => (row.original.amount ? formatCurrency(row.original.amount) : 'N/A'),
    },
    // {
    //   id: 'utr',
    //   header: 'UTR Number',
    //   accessorKey: 'utr',
    //   size: 110,
    //   cell: ({ row }: { row: any }) => (
    //     <div className="flex flex-col">
    //       <span>{row.original.utr || 'N/A'}</span>
    //       {row.original.utrUpdatedAt && <span className="text-[11px] text-gray-500">{formatTimestamp(row.original.utrUpdatedAt)}</span>}
    //     </div>
    //   ),
    // },
    {
      id: 'orderCreatedAt',
      header: 'Order Date',
      accessorKey: 'orderCreatedAt',
      size: 120,
      cell: ({ row }: { row: any }) => (
        <div className="flex items-center">
          <Calendar size={14} className="mr-1 text-gray-500" />
          <span>{row.original.orderCreatedAt ? formatTimestampGateway(row.original.orderCreatedAt) : 'N/A'}</span>
        </div>
      ),
    },
  ];

  // Table cell padding styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .orders-table td { padding: 4px 6px !important; }
      .orders-table th { padding: 4px 6px !important; font-size: 0.7rem !important; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Row click → fetch order details
  const handleRowClick = async (order: Order) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const lenderCode = user?.code ? String(user.code).toLowerCase() : '';
      const orderDetail = await fetchOrderById(
        order.orderId,
        lenderCode,
        token,

      );
      if (!orderDetail) throw new Error('Failed to fetch order details');

      const transformedOrder: OrderDetail = {
        ...orderDetail,
        id: orderDetail._id,
        paymentLink: orderDetail.paymentLink || '',
        redirectionLink: orderDetail.redirectionLink || '',
        status: orderDetail.status as OrderStatus,
        utrUpdatedAt: orderDetail.utrUpdatedAt ? Number(orderDetail.utrUpdatedAt) : 0, // ✅ fallback
        orderCreatedAt: orderDetail.orderCreatedAt ?? 0, // ✅ fallback
        productDetails: (orderDetail as any).productDetails || '',
        docCustomerConsent: (orderDetail as any).docCustomerConsent || '',
        docCustomerConsentAt: (orderDetail as any).docCustomerConsentAt || '',
        variationString: (orderDetail as any).variationString || '',
        docProduct: orderDetail.docProduct || '',
        docProductAt: orderDetail.docProductAt || '',
        docReceipt: orderDetail.docReceipt || '',
        docReceiptAt: orderDetail.docReceiptAt || '',
        docVehicleChassis: orderDetail.docVehicleChassis || '',
        docVehicleChassisAt: orderDetail.docVehicleChassisAt || '',
        docVehicleMotor: orderDetail.docVehicleMotor || '',
        docVehicleMotorAt: orderDetail.docVehicleMotorAt || '',
        docRtoAcknowledgement: orderDetail.docRtoAcknowledgement || '',
        docRtoAcknowledgementAt: orderDetail.docRtoAcknowledgementAt || '',
        docInsurance: orderDetail.docInsurance || '',
        docInsuranceAt: orderDetail.docInsuranceAt || '',
        amount: typeof orderDetail.amount === 'number' ? orderDetail.amount : 0,
        documents: [
          orderDetail.docProduct ? { _id: `${orderDetail._id}-product`, type: 'product', url: orderDetail.docProduct, name: 'Product Document', docType: 'docProduct' } : null,
          orderDetail.docReceipt ? { _id: `${orderDetail._id}-receipt`, type: 'receipt', url: orderDetail.docReceipt, name: 'Receipt Document', docType: 'docReceipt' } : null,
          (orderDetail as any).docCustomerConsent ? { _id: `${orderDetail._id}-consent`, type: 'consent', url: (orderDetail as any).docCustomerConsent, name: 'Customer Consent', docType: 'docCustomerConsent' } : null,
        ].filter(Boolean) as OrderDocument[],
        payment: (orderDetail as any).payment || {},
        transaction: {
          eligibleTenure: orderDetail.transaction?.eligibleTenure || [],
          transactionId: orderDetail.transaction?.transactionId || null,
          transactionStatus: orderDetail.transaction?.transactionStatus || null,
          transactionTime: orderDetail.transaction?.transactionTime || null,
          leafRefNo: orderDetail.transaction?.leafRefNo || null,
        },
        product: (orderDetail as any).productId || null,
        customerId: typeof orderDetail.customerId === 'object' && orderDetail.customerId !== null ? orderDetail.customerId : {
          _id: '',
          name: orderDetail.settlement?.customerName || '',
          mobileNo: orderDetail.settlement?.customerMobileNo || '',
        },
      };


      setSelectedOrder(transformedOrder);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setSelectedOrder(null);
      setDetailsOpen(false);
      showToast.error(error instanceof Error ? error.message : 'Failed to fetch order details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedOrder(null);
  };

  // Filter handlers
  const handleFilterChange = (key: keyof OrderFilters, value: any) => {
    setTempFilters(prev => ({
      ...(typeof prev === 'object' && prev !== null ? prev : {}),
      [key]: {
        ...(typeof prev?.[key] === 'object' && prev[key] !== null ? prev[key] : {}),
        ...value
      }
    }));
  };

  const handleResetFilters = () => {
    setTempFilters({
      dateRange: { from: "", to: "" } // ✅ use empty string
    });
    setFilters({});
    setPage(1);
    fetchOrdersData();
  };



  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
    fetchOrdersData();
    setShowFilters(false);
  };

  return (
    <div className="w-full no-scrollbar">
      {/* Filters */}
      <div className="mb-4">
        <div className="flex justify-start lg:justify-end items-center mb-4">
          <Button variant="outline" onClick={() => setShowFilters(prev => !prev)} className="flex items-center" size="sm">
            <Filter size={14} className="mr-1" />
            Filters
            <ChevronDown size={14} className="ml-1" />
          </Button>
        </div>

        {showFilters && (
          <div className="bg-white p-4 rounded-lg shadow-sm mb-4 border border-gray-200">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-4">
                <label className="block text-sm text-gray-700 mb-2">From Date</label>
                <input
                  type="date"
                  value={formatDateForInput(tempFilters.dateRange?.from || "")}
                  onChange={(e) => handleFilterChange('dateRange', { from: e.target.value || "" })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <label className="block text-sm text-gray-700 mb-2">To Date</label>
                <input
                  type="date"
                  value={formatDateForInput(tempFilters.dateRange?.to || "")}
                  onChange={(e) => handleFilterChange('dateRange', { to: e.target.value || "" })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="col-span-12 md:col-span-4 flex items-end justify-end">
                <Button variant="outline" onClick={handleResetFilters} size="sm" className="mr-2 px-4">Reset</Button>
                <Button onClick={handleApplyFilters} size="sm" className="px-4">Apply</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={orders}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          total={totalMatch}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSortChange={(columnId, order) => {
            setSortBy(columnId);
            setSortOrder(order);
          }}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search orders..."
          enableRowClick
          onRowClick={handleRowClick}
          className="orders-table no-scrollbar"
        />
      </div>




      {selectedOrder && (
        <OrderDetailModal isOpen={detailsOpen} onClose={handleCloseDetails} order={selectedOrder} />
      )}
    </div>
  );
};

export default OrderTab;
